import { TextHelper } from './text.helper';

/**
 * Markup the code has explicitly vouched for. Consumers that render it into the DOM as it is - the
 * popup service, which hands its text to SweetAlert2 - escape every plain string they get and skip
 * escaping only for this type, so the unsafe path cannot be taken by accident. The `html` tagged
 * template below is the only way to build one.
 */
export class TrustedHtml {
  constructor(readonly value: string) {}

  toString(): string {
    return this.value;
  }
}

/** What may be interpolated into the `html` template below. A value that is not there renders as nothing. */
export type HtmlValue = string | number | TrustedHtml | null | undefined;

/**
 * Tagged template for text that needs markup. The literal parts are written by us and kept as they
 * are; every interpolated value is HTML-escaped, so entity names cannot inject DOM. Nested
 * {@link TrustedHtml} values are inserted as they are, which makes fragments composable.
 *
 * @example
 * html`Are you sure you want to delete <b>${workflow.label}</b>?`
 */
export function html(literals: TemplateStringsArray, ...values: HtmlValue[]): TrustedHtml {
  return new TrustedHtml(literals.reduce((text, literal, index) => text + encodeValue(values[index - 1]) + literal));
}

/**
 * Joins fragments with markup between them, keeping the result trusted. Fragments that are not
 * there are left out, so an optional part can simply be `null`.
 *
 * @example
 * joinHtml([processesPart, machinesPart], html`<br /><br />`)
 */
export function joinHtml(fragments: (TrustedHtml | null | undefined)[], separator: TrustedHtml): TrustedHtml {
  return new TrustedHtml(fragments.filter(fragment => fragment != null).map(fragment => fragment.value).join(separator.value));
}

function encodeValue(value: HtmlValue): string {
  if (value == null) {
    return '';
  }

  return value instanceof TrustedHtml ? value.value : TextHelper.encodeHtml(String(value));
}
